import type { FastifyReply, FastifyRequest } from 'fastify'
import type {
  CreateLiveSessionPayload,
  JoinLiveSessionPayload,
  UpdatePositionPayload,
  SendChatMessagePayload,
  EmergencyAlertPayload,
  TelemetrySamplePayload,
  LapTime
} from '@my-roadtrip/types'
import { ApiResponse } from '../../utils/response.js'
import { createLogger } from '../../common/utils/logger.js'
import { LiveNavigationService } from './liveNavigation.service.js'

const logger = createLogger('LiveNavigationController')

type SessionIdParams = { sessionId: string }
type ParticipantParams = { sessionId: string; userId: string }
type ExportQuerystring = { format: 'gpx' | 'csv' | 'json' }

export class LiveNavigationController {
  constructor(private readonly service: LiveNavigationService) {}

  // ==========================================
  // SESSION MANAGEMENT
  // ==========================================

  async createSession(request: FastifyRequest<{ Body: CreateLiveSessionPayload }>, reply: FastifyReply) {
    const userId = request.user?.uid
    const userName = request.user?.name || 'Utilisateur'
    const userTier = (request.user as any)?.tier || 'free'

    if (!userId) {
      this.warnUnauthorized('createSession', request)
      return ApiResponse.unauthorized(reply)
    }

    const { spanId, context } = this.startRequestSpan('createSession', request, {
      sessionType: request.body?.type,
      tier: userTier
    })

    try {
      const session = await this.service.createSession(
        userId,
        userName,
        request.body,
        userTier
      )

      const successContext = { ...context, sessionId: session.id }
      logger.info('Live session created', successContext)
      this.endRequestSpan(spanId, { ...successContext, success: true })
      return reply.code(201).send({ session })
    } catch (error: any) {
      logger.error('Failed to create session', { ...context, error })
      this.endRequestSpan(spanId, { ...context, success: false, error: error?.message })
      return ApiResponse.error(reply, 500, error?.message || 'Failed to create session')
    }
  }

  async getSession(request: FastifyRequest<{ Params: SessionIdParams }>, reply: FastifyReply) {
    const userId = request.user?.uid

    if (!userId) {
      this.warnUnauthorized('getSession', request)
      return ApiResponse.unauthorized(reply)
    }

    const { spanId, context } = this.startRequestSpan('getSession', request, {
      sessionId: request.params.sessionId
    })

    try {
      const session = await this.service.getSession(request.params.sessionId, userId)

      if (!session) {
        logger.warn('Session not found', context)
        this.endRequestSpan(spanId, { ...context, success: false, reason: 'not_found' })
        return ApiResponse.notFound(reply, 'Session not found')
      }

      const successContext = { ...context, participantCount: session.participants.length }
      logger.info('Session returned', successContext)
      this.endRequestSpan(spanId, { ...successContext, success: true })
      return reply.send({ session })
    } catch (error: any) {
      logger.error('Failed to get session', { ...context, error })
      this.endRequestSpan(spanId, { ...context, success: false, error: error?.message })
      return ApiResponse.error(reply, 500, error?.message || 'Failed to get session')
    }
  }

  async listSessions(request: FastifyRequest, reply: FastifyReply) {
    const userId = request.user?.uid

    if (!userId) {
      this.warnUnauthorized('listSessions', request)
      return ApiResponse.unauthorized(reply)
    }

    const { spanId, context } = this.startRequestSpan('listSessions', request)

    try {
      const sessions = await this.service.listSessions(userId)
      const successContext = { ...context, count: sessions.length }
      logger.info('Active sessions listed', successContext)
      this.endRequestSpan(spanId, { ...successContext, success: true })
      return reply.send({ sessions })
    } catch (error: any) {
      logger.error('Failed to list sessions', { ...context, error })
      this.endRequestSpan(spanId, { ...context, success: false, error: error?.message })
      return ApiResponse.error(reply, 500, error?.message || 'Failed to list sessions')
    }
  }

  async endSession(request: FastifyRequest<{ Params: SessionIdParams }>, reply: FastifyReply) {
    const userId = request.user?.uid

    if (!userId) {
      this.warnUnauthorized('endSession', request)
      return ApiResponse.unauthorized(reply)
    }

    const { spanId, context } = this.startRequestSpan('endSession', request, {
      sessionId: request.params.sessionId
    })

    try {
      await this.service.endSession(request.params.sessionId, userId)
      logger.info('Session end requested', context)
      this.endRequestSpan(spanId, { ...context, success: true })
      return reply.send({ success: true })
    } catch (error: any) {
      logger.error('Failed to end session', { ...context, error })
      this.endRequestSpan(spanId, { ...context, success: false, error: error?.message })
      return ApiResponse.error(reply, 500, error?.message || 'Failed to end session')
    }
  }

  // ==========================================
  // PARTICIPANT MANAGEMENT
  // ==========================================

  async joinSession(
    request: FastifyRequest<{ Params: SessionIdParams; Body: JoinLiveSessionPayload }>,
    reply: FastifyReply
  ) {
    const userId = request.user?.uid
    const userName = request.user?.name || 'Utilisateur'
    const userAvatar = (request.user as any)?.picture || (request.user as any)?.photoURL

    if (!userId) {
      this.warnUnauthorized('joinSession', request)
      return ApiResponse.unauthorized(reply)
    }

    const { spanId, context } = this.startRequestSpan('joinSession', request, {
      sessionId: request.params.sessionId,
      requestedRole: request.body?.role
    })

    try {
      const participant = await this.service.joinSession(
        request.params.sessionId,
        userId,
        userName,
        userAvatar,
        request.body
      )

      const successContext = { ...context, participantRole: participant.role }
      logger.info('Participant joined session', successContext)
      this.endRequestSpan(spanId, { ...successContext, success: true })
      return reply.send({ participant })
    } catch (error: any) {
      logger.error('Failed to join session', { ...context, error })
      this.endRequestSpan(spanId, { ...context, success: false, error: error?.message })
      return ApiResponse.error(reply, 500, error?.message || 'Failed to join session')
    }
  }

  async leaveSession(request: FastifyRequest<{ Params: SessionIdParams }>, reply: FastifyReply) {
    const userId = request.user?.uid

    if (!userId) {
      this.warnUnauthorized('leaveSession', request)
      return ApiResponse.unauthorized(reply)
    }

    const { spanId, context } = this.startRequestSpan('leaveSession', request, {
      sessionId: request.params.sessionId
    })

    try {
      await this.service.leaveSession(request.params.sessionId, userId)
      logger.info('Participant left session', context)
      this.endRequestSpan(spanId, { ...context, success: true })
      return reply.send({ success: true })
    } catch (error: any) {
      logger.error('Failed to leave session', { ...context, error })
      this.endRequestSpan(spanId, { ...context, success: false, error: error?.message })
      return ApiResponse.error(reply, 500, error?.message || 'Failed to leave session')
    }
  }

  async updatePosition(
    request: FastifyRequest<{ Params: SessionIdParams; Body: UpdatePositionPayload }>,
    reply: FastifyReply
  ) {
    const userId = request.user?.uid

    if (!userId) {
      this.warnUnauthorized('updatePosition', request)
      return ApiResponse.unauthorized(reply)
    }

    const { spanId, context } = this.startRequestSpan('updatePosition', request, {
      sessionId: request.params.sessionId,
      status: request.body?.status,
      speed: request.body?.position?.speed,
      accuracy: request.body?.position?.accuracy
    })

    try {
      await this.service.updatePosition(request.params.sessionId, userId, request.body)
      logger.debug('Position update accepted', context)
      this.endRequestSpan(spanId, { ...context, success: true })
      return reply.send({ success: true })
    } catch (error: any) {
      if (error?.message === 'Not a participant') {
        logger.warn('Position update rejected – user not participant', { ...context, error })
      } else {
        logger.debug('Position update failed', { ...context, error })
      }
      this.endRequestSpan(spanId, { ...context, success: false, error: error?.message })
      return ApiResponse.error(reply, 500, error?.message || 'Failed to update position')
    }
  }

  async updateParticipantRole(
    request: FastifyRequest<{ Params: ParticipantParams; Body: { role: string } }>,
    reply: FastifyReply
  ) {
    const userId = request.user?.uid

    if (!userId) {
      this.warnUnauthorized('updateParticipantRole', request)
      return ApiResponse.unauthorized(reply)
    }

    const { spanId, context } = this.startRequestSpan('updateParticipantRole', request, {
      sessionId: request.params.sessionId,
      targetUserId: request.params.userId,
      role: request.body.role
    })

    try {
      await this.service.updateParticipantRole(
        request.params.sessionId,
        request.params.userId,
        request.body.role,
        userId
      )

      logger.info('Participant role updated', context)
      this.endRequestSpan(spanId, { ...context, success: true })
      return reply.send({ success: true })
    } catch (error: any) {
      logger.error('Failed to update role', { ...context, error })
      this.endRequestSpan(spanId, { ...context, success: false, error: error?.message })
      return ApiResponse.error(reply, 500, error?.message || 'Failed to update role')
    }
  }

  async removeParticipant(
    request: FastifyRequest<{ Params: ParticipantParams }>,
    reply: FastifyReply
  ) {
    const userId = request.user?.uid

    if (!userId) {
      this.warnUnauthorized('removeParticipant', request)
      return ApiResponse.unauthorized(reply)
    }

    const { spanId, context } = this.startRequestSpan('removeParticipant', request, {
      sessionId: request.params.sessionId,
      targetUserId: request.params.userId
    })

    try {
      await this.service.removeParticipant(
        request.params.sessionId,
        request.params.userId,
        userId
      )

      logger.info('Participant removal requested', context)
      this.endRequestSpan(spanId, { ...context, success: true })
      return reply.send({ success: true })
    } catch (error: any) {
      logger.error('Failed to remove participant', { ...context, error })
      this.endRequestSpan(spanId, { ...context, success: false, error: error?.message })
      return ApiResponse.error(reply, 500, error?.message || 'Failed to remove participant')
    }
  }

  // ==========================================
  // CHAT
  // ==========================================

  async sendChatMessage(
    request: FastifyRequest<{ Params: SessionIdParams; Body: SendChatMessagePayload }>,
    reply: FastifyReply
  ) {
    const userId = request.user?.uid
    const userName = request.user?.name || 'Utilisateur'
    const userAvatar = (request.user as any)?.picture || (request.user as any)?.photoURL

    if (!userId) {
      this.warnUnauthorized('sendChatMessage', request)
      return ApiResponse.unauthorized(reply)
    }

    const { spanId, context } = this.startRequestSpan('sendChatMessage', request, {
      sessionId: request.params.sessionId,
      messageType: request.body?.type
    })

    try {
      const message = await this.service.sendChatMessage(
        request.params.sessionId,
        userId,
        userName,
        userAvatar,
        request.body
      )

      const successContext = { ...context, messageId: message.id }
      logger.info('Chat message sent', successContext)
      this.endRequestSpan(spanId, { ...successContext, success: true })
      return reply.send({ message })
    } catch (error: any) {
      logger.error('Failed to send message', { ...context, error })
      this.endRequestSpan(spanId, { ...context, success: false, error: error?.message })
      return ApiResponse.error(reply, 500, error?.message || 'Failed to send message')
    }
  }

  async getChatHistory(request: FastifyRequest<{ Params: SessionIdParams }>, reply: FastifyReply) {
    const userId = request.user?.uid

    if (!userId) {
      this.warnUnauthorized('getChatHistory', request)
      return ApiResponse.unauthorized(reply)
    }

    const { spanId, context } = this.startRequestSpan('getChatHistory', request, {
      sessionId: request.params.sessionId
    })

    try {
      const messages = await this.service.getChatHistory(request.params.sessionId, userId)
      const successContext = { ...context, count: messages.length }
      logger.info('Chat history fetched', successContext)
      this.endRequestSpan(spanId, { ...successContext, success: true })
      return reply.send({ messages })
    } catch (error: any) {
      logger.error('Failed to get chat history', { ...context, error })
      this.endRequestSpan(spanId, { ...context, success: false, error: error?.message })
      return ApiResponse.error(reply, 500, error?.message || 'Failed to get chat history')
    }
  }

  // ==========================================
  // SAFETY & ALERTS
  // ==========================================

  async sendEmergencyAlert(
    request: FastifyRequest<{ Params: SessionIdParams; Body: EmergencyAlertPayload }>,
    reply: FastifyReply
  ) {
    const userId = request.user?.uid

    if (!userId) {
      this.warnUnauthorized('sendEmergencyAlert', request)
      return ApiResponse.unauthorized(reply)
    }

    const { spanId, context } = this.startRequestSpan('sendEmergencyAlert', request, {
      sessionId: request.params.sessionId,
      requiresHelp: request.body?.requiresHelp
    })

    try {
      await this.service.sendEmergencyAlert(request.params.sessionId, userId, request.body)
      logger.warn('Emergency alert forwarded', context)
      this.endRequestSpan(spanId, { ...context, success: true })
      return reply.send({ success: true })
    } catch (error: any) {
      logger.error('Failed to send emergency alert', { ...context, error })
      this.endRequestSpan(spanId, { ...context, success: false, error: error?.message })
      return ApiResponse.error(reply, 500, error?.message || 'Failed to send emergency alert')
    }
  }

  async clearEmergencyAlert(request: FastifyRequest<{ Params: SessionIdParams }>, reply: FastifyReply) {
    const userId = request.user?.uid

    if (!userId) {
      this.warnUnauthorized('clearEmergencyAlert', request)
      return ApiResponse.unauthorized(reply)
    }

    const { spanId, context } = this.startRequestSpan('clearEmergencyAlert', request, {
      sessionId: request.params.sessionId
    })

    try {
      await this.service.clearEmergencyAlert(request.params.sessionId, userId)
      logger.info('Emergency alert cleared', context)
      this.endRequestSpan(spanId, { ...context, success: true })
      return reply.send({ success: true })
    } catch (error: any) {
      logger.error('Failed to clear emergency', { ...context, error })
      this.endRequestSpan(spanId, { ...context, success: false, error: error?.message })
      return ApiResponse.error(reply, 500, error?.message || 'Failed to clear emergency')
    }
  }

  // ==========================================
  // TELEMETRY & TRACK DAYS
  // ==========================================

  async recordTelemetry(
    request: FastifyRequest<{ Params: SessionIdParams; Body: TelemetrySamplePayload }>,
    reply: FastifyReply
  ) {
    const userId = request.user?.uid

    if (!userId) {
      this.warnUnauthorized('recordTelemetry', request)
      return ApiResponse.unauthorized(reply)
    }

    const telemetryPayload = request.body
    const { spanId, context } = this.startRequestSpan('recordTelemetry', request, {
      sessionId: request.params.sessionId,
      sampleRate: telemetryPayload?.sampleRate
    })

    try {
      await this.service.recordTelemetry(request.params.sessionId, userId, telemetryPayload)
      logger.debug('Telemetry payload recorded', context)
      this.endRequestSpan(spanId, { ...context, success: true })
      return reply.send({ success: true })
    } catch (error: any) {
      logger.error('Failed to record telemetry', { ...context, error })
      this.endRequestSpan(spanId, { ...context, success: false, error: error?.message })
      return ApiResponse.error(reply, 500, error?.message || 'Failed to record telemetry')
    }
  }

  async completeLap(
    request: FastifyRequest<{ Params: SessionIdParams; Body: Partial<LapTime> }>,
    reply: FastifyReply
  ) {
    const userId = request.user?.uid

    if (!userId) {
      this.warnUnauthorized('completeLap', request)
      return ApiResponse.unauthorized(reply)
    }

    const lapPayload = request.body
    const { spanId, context } = this.startRequestSpan('completeLap', request, {
      sessionId: request.params.sessionId,
      lap: lapPayload?.lap
    })

    try {
      const lap = await this.service.completeLap(request.params.sessionId, userId, lapPayload ?? {})
      const successContext = { ...context, lapId: lap.id }
      logger.info('Lap completed', successContext)
      this.endRequestSpan(spanId, { ...successContext, success: true })
      return reply.send({ lap })
    } catch (error: any) {
      logger.error('Failed to complete lap', { ...context, error })
      this.endRequestSpan(spanId, { ...context, success: false, error: error?.message })
      return ApiResponse.error(reply, 500, error?.message || 'Failed to complete lap')
    }
  }

  async getLeaderboard(request: FastifyRequest<{ Params: SessionIdParams }>, reply: FastifyReply) {
    const userId = request.user?.uid

    if (!userId) {
      this.warnUnauthorized('getLeaderboard', request)
      return ApiResponse.unauthorized(reply)
    }

    const { spanId, context } = this.startRequestSpan('getLeaderboard', request, {
      sessionId: request.params.sessionId
    })

    try {
      const leaderboard = await this.service.getLeaderboard(request.params.sessionId, userId)
      const successContext = { ...context, count: leaderboard.length }
      logger.info('Leaderboard fetched', successContext)
      this.endRequestSpan(spanId, { ...successContext, success: true })
      return reply.send({ leaderboard })
    } catch (error: any) {
      logger.error('Failed to get leaderboard', { ...context, error })
      this.endRequestSpan(spanId, { ...context, success: false, error: error?.message })
      return ApiResponse.error(reply, 500, error?.message || 'Failed to get leaderboard')
    }
  }

  // ==========================================
  // REPLAY & EXPORT
  // ==========================================

  async getReplay(request: FastifyRequest<{ Params: SessionIdParams }>, reply: FastifyReply) {
    const userId = request.user?.uid

    if (!userId) {
      this.warnUnauthorized('getReplay', request)
      return ApiResponse.unauthorized(reply)
    }

    const { spanId, context } = this.startRequestSpan('getReplay', request, {
      sessionId: request.params.sessionId
    })

    try {
      const replay = await this.service.getReplay(request.params.sessionId, userId)
      logger.info('Replay payload generated', context)
      this.endRequestSpan(spanId, { ...context, success: true })
      return reply.send({ replay })
    } catch (error: any) {
      logger.error('Failed to get replay', { ...context, error })
      this.endRequestSpan(spanId, { ...context, success: false, error: error?.message })
      return ApiResponse.error(reply, 500, error?.message || 'Failed to get replay')
    }
  }

  async exportSession(
    request: FastifyRequest<{ Params: SessionIdParams; Querystring: ExportQuerystring }>,
    reply: FastifyReply
  ) {
    const userId = request.user?.uid

    if (!userId) {
      this.warnUnauthorized('exportSession', request)
      return ApiResponse.unauthorized(reply)
    }

    const { spanId, context } = this.startRequestSpan('exportSession', request, {
      sessionId: request.params.sessionId,
      format: request.query.format
    })

    try {
      const data = await this.service.exportSession(
        request.params.sessionId,
        userId,
        request.query.format
      )

      // Set content type selon format
      const contentTypes = {
        gpx: 'application/gpx+xml',
        csv: 'text/csv',
        json: 'application/json'
      }

      reply.header('Content-Type', contentTypes[request.query.format])
      reply.header('Content-Disposition', `attachment; filename="session-${request.params.sessionId}.${request.query.format}"`)

      logger.info('Session export generated', { ...context, size: (data as any)?.length })
      this.endRequestSpan(spanId, { ...context, success: true })
      return reply.send(data)
    } catch (error: any) {
      logger.error('Failed to export session', { ...context, error })
      this.endRequestSpan(spanId, { ...context, success: false, error: error?.message })
      return ApiResponse.error(reply, 500, error?.message || 'Failed to export session')
    }
  }

  // ==========================================
  // STATS
  // ==========================================

  async getSessionStats(request: FastifyRequest<{ Params: SessionIdParams }>, reply: FastifyReply) {
    const userId = request.user?.uid

    if (!userId) {
      this.warnUnauthorized('getSessionStats', request)
      return ApiResponse.unauthorized(reply)
    }

    const { spanId, context } = this.startRequestSpan('getSessionStats', request, {
      sessionId: request.params.sessionId
    })

    try {
      const stats = await this.service.getSessionStats(request.params.sessionId, userId)
      logger.info('Session stats retrieved', { ...context, activeParticipants: stats.activeParticipants })
      this.endRequestSpan(spanId, { ...context, success: true })
      return reply.send({ stats })
    } catch (error: any) {
      logger.error('Failed to get stats', { ...context, error })
      this.endRequestSpan(spanId, { ...context, success: false, error: error?.message })
      return ApiResponse.error(reply, 500, error?.message || 'Failed to get stats')
    }
  }

  private warnUnauthorized(operation: string, request: FastifyRequest) {
    logger.warn(`Unauthorized ${operation} request`, {
      operation,
      requestId: String(request.id ?? ''),
      ip: request.ip
    })
  }

  private startRequestSpan(
    operation: string,
    request: FastifyRequest,
    extraContext: Record<string, unknown> = {}
  ) {
    const context = {
      operation,
      requestId: String(request.id ?? ''),
      userId: request.user?.uid,
      method: request.method,
      ip: request.ip,
      ...extraContext
    }

    const spanId = logger.startSpan(`LiveNavigationController.${operation}`, context)
    logger.debug(`${operation} request received`, context)
    return { spanId, context }
  }

  private endRequestSpan(spanId: string, result?: Record<string, unknown>) {
    logger.endSpan(spanId, result)
  }
}
